import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { 
  CalendarDays, 
  MapPin, 
  Users, 
  Check, 
  HelpCircle,
  Mail,
  Linkedin,
  ChevronLeft,
  Search,
  Ticket,
  Loader2,
  SlidersHorizontal,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { createPageUrl, normalizePlaybookKey } from '@/utils';
import { OUTBOX_POST_GENERATION_NAV_STATE } from '@/lib/outboxPostGenerationNav';
import {
  aggregateParsedList,
  describeSequencesFailed,
  fetchPlaybookStatusBodiesAllSettled,
  formatGeneratingProgressLine,
  normalizeRunSequenceIds,
  parsePlaybookStatusData,
  sequencePollPhase,
} from '@/lib/playbookStatusAggregate';
import { toast } from 'sonner';
import {
  EVENT_PLAYBOOK_DISPLAY,
  getEventPlaybookDisplay,
  getEligibilityBlockMessage,
  getEventConfirmedAttendeeCount,
} from '@/lib/eventPlaybookDisplay';
import EventCustomPlaybookStatusBanner from '@/components/events/EventCustomPlaybookStatusBanner';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

const CREATE_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const INITIAL_POLL_DELAY_MS = 2000;
const FAST_POLL_INTERVAL_MS = 5000;
const SLOW_POLL_INTERVAL_MS = 10000;
const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;
const MIN_WATCH_WINDOW_MS = 15 * 60 * 1000;
const MAX_WATCH_WINDOW_MS = 60 * 60 * 1000;
const WATCH_WINDOW_PER_MESSAGE_MS = 20 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const getPollIntervalMs = (elapsedMs) => (elapsedMs < FAST_POLL_WINDOW_MS ? FAST_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS);
const computeWatchWindowMs = (estimatedMessages) => {
  const safeMessages = Number.isFinite(estimatedMessages) && estimatedMessages > 0 ? estimatedMessages : 0;
  return clamp(safeMessages * WATCH_WINDOW_PER_MESSAGE_MS, MIN_WATCH_WINDOW_MS, MAX_WATCH_WINDOW_MS);
};

const channelLabel = (channel) => {
  const c = String(channel || '').toLowerCase();
  if (c === 'linkedin_dm') return 'linkedin_dm';
  return c || 'unknown';
};

const normalizeCadenceAdaptation = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    compressed: !!raw.compressed,
    scaling_applied: !!raw.scaling_applied,
    days_until_event: Number(raw.days_until_event ?? 0),
    configured_pre_event_days: Number(raw.configured_pre_event_days ?? 0),
    effective_lead_days: Number(raw.effective_lead_days ?? 0),
    dropped: Array.isArray(raw.dropped) ? raw.dropped : [],
  };
};

export default function EventPlaybookPreview({ currentTenant: currentTenantProp }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const user = contextUser;
  
  const eventId = searchParams.get('event_id');
  const playbookKeyRaw = searchParams.get('playbook_key');
  const playbookKey = String(playbookKeyRaw || '').toLowerCase().startsWith('event::')
    ? String(playbookKeyRaw || '').trim()
    : normalizePlaybookKey(playbookKeyRaw);
  
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
  const [useSignals, setUseSignals] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [createCadenceAdaptation, setCreateCadenceAdaptation] = useState(null);
  const [sortBy, setSortBy] = useState('persona_fit_score');
  const [filterArchetype, setFilterArchetype] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterScoreMin, setFilterScoreMin] = useState(0);
  const [hideWithMessages, setHideWithMessages] = useState(false);
  const [blockedRetryItems, setBlockedRetryItems] = useState([]);
  const [blockedRetryErrors, setBlockedRetryErrors] = useState({});
  const [retryingStepIds, setRetryingStepIds] = useState([]);
  const [blockedRetryCount, setBlockedRetryCount] = useState(0);
  const blockedToastIdRef = useRef(null);
  const blockedToastFiredRef = useRef(false);
  const pollCancelledRef = useRef(false);
  const activePollAbortControllerRef = useRef(null);
  
  // Helper to determine if a persona has been messaged (defined at top level)
  const isMessagedPersona = (p) => {
    const status = (p?.message_status || '').toLowerCase();
    return Boolean(p?.has_message_generated) || ['draft', 'draft_ready', 'scheduled', 'sent'].includes(status);
  };

  const formatBlockedStepLabel = (step) => {
    const name = step?.persona_name || 'Unknown persona';
    const touch = step?.step_number ? `Touch ${step.step_number}` : 'Touch ?';
    const channel = step?.channel || 'unknown channel';
    return `${name} (${touch}, ${channel})`;
  };

  const retryStep = async (stepId) => {
    if (!stepId || !contextUser?.id) return;
    setRetryingStepIds((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
    setBlockedRetryErrors((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
    try {
      const r = await fetch(`${API_BASE_URL}/api/playbooks/regenerate-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step_id: stepId, tenant_id: tenantId, user_id: contextUser.id }),
      });
      const data = await r.json().catch(() => ({}));
      if (data?.success) {
        setBlockedRetryItems((prev) => prev.filter((item) => item?.step_id !== stepId));
        setBlockedRetryErrors((prev) => {
          const next = { ...prev };
          delete next[stepId];
          return next;
        });
      } else {
        setBlockedRetryErrors((prev) => ({
          ...prev,
          [stepId]: data?.qa_issues?.[0] || data?.reason || 'Still failing',
        }));
      }
    } catch (error) {
      setBlockedRetryErrors((prev) => ({
        ...prev,
        [stepId]: error?.message || 'Still failing',
      }));
    } finally {
      setRetryingStepIds((prev) => prev.filter((id) => id !== stepId));
    }
  };

  useEffect(() => {
    return () => {
      pollCancelledRef.current = true;
      if (activePollAbortControllerRef.current) {
        activePollAbortControllerRef.current.abort();
        activePollAbortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!blockedRetryItems.length) {
      if (blockedToastIdRef.current) {
        toast.dismiss(blockedToastIdRef.current);
        blockedToastIdRef.current = null;
      }
      return;
    }

    if (blockedToastIdRef.current) {
      toast.dismiss(blockedToastIdRef.current);
    }

    blockedToastIdRef.current = toast.error(
      `⚠️  ${blockedRetryCount || blockedRetryItems.length} message(s) couldn't be generated. Retry below.`,
      {
        duration: Infinity,
        description: (
          <div className="space-y-2 mt-2">
            {blockedRetryItems.map((step) => {
              const stepId = step?.step_id;
              const retrying = retryingStepIds.includes(stepId);
              return (
                <div key={stepId || `${step?.person_id || 'unknown'}-${step?.step_number || 'na'}`} className="border border-red-200 rounded p-2 bg-red-50/60">
                  <div className="text-xs">{formatBlockedStepLabel(step)}</div>
                  {blockedRetryErrors[stepId] && (
                    <div className="text-[11px] text-red-700 mt-1">{blockedRetryErrors[stepId]}</div>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    className="mt-2 h-7 text-xs"
                    onClick={() => retryStep(stepId)}
                    disabled={!stepId || retrying}
                  >
                    {retrying ? 'Retrying...' : 'Retry'}
                  </Button>
                </div>
              );
            })}
          </div>
        ),
      }
    );
  }, [blockedRetryItems, blockedRetryErrors, retryingStepIds, blockedRetryCount]);

  // Debug: Log when hideWithMessages changes
  useEffect(() => {
    console.log('🔍 hideWithMessages state changed to:', hideWithMessages);
  }, [hideWithMessages]);
  
  useEffect(() => {
    if (eventId && playbookKey && contextUser) {
      console.log('🔄 Fetching preview:', { eventId, playbookKey, currentTenant });
      fetchPreview();
    } else if (!eventId || !playbookKey) {
      console.warn('⚠️ Missing required params:', { eventId, playbookKey });
    }
  }, [eventId, playbookKey, currentTenant, contextUser]);

  // ✅ MUST be above any early returns - Auto-deselect messaged personas when hiding them
  useEffect(() => {
    if (!hideWithMessages || !previewData) return;

    const confirmedArr = Array.isArray(previewData.confirmed_attendees) ? previewData.confirmed_attendees : [];
    const potentialArr = Array.isArray(previewData.potential_attendees) ? previewData.potential_attendees : [];

    const all = [
      ...confirmedArr,
      ...potentialArr,
    ];

    const messagedIds = new Set(all.filter(isMessagedPersona).map(p => p.person_id));

    setSelectedPersonaIds(prev => prev.filter(id => !messagedIds.has(id)));
  }, [hideWithMessages, previewData]);
  
  // Add error boundary effect to catch any rendering errors
  useEffect(() => {
    const handleError = (event) => {
      console.error('❌ Global error caught:', event.error);
      console.error('❌ Error details:', {
        message: event.error?.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    };
    
    const handleUnhandledRejection = (event) => {
      console.error('❌ Unhandled promise rejection:', event.reason);
      console.error('❌ Rejection details:', {
        reason: event.reason,
        promise: event.promise
      });
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  async function fetchPreview() {
    if (!contextUser) return;
    console.log('📥 fetchPreview called');
    setLoading(true);
    try {
      console.log('✅ User from context:', { id: contextUser?.id, email: contextUser?.email });
      
      const url = `${API_BASE_URL}/api/event-playbooks/preview?event_id=${eventId}&playbook_key=${encodeURIComponent(playbookKey)}&user_id=${contextUser.id}&tenant_id=${tenantId}`;
      console.log('🌐 Fetching preview from:', url);
      
      const response = await fetch(url, { credentials: 'include' });
      
      console.log('📡 Preview response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Preview fetch failed:', errorData);
        throw new Error(errorData.error || 'Failed to load preview');
      }
      
      const data = await response.json();
      console.log('📦 Preview data received:', {
        hasEvent: !!data.event,
        hasPlaybook: !!data.playbook,
        confirmedCount: data.confirmed_attendees?.length || 0,
        potentialCount: data.potential_attendees?.length || 0,
        hasMessageStats: !!data.message_stats
      });
      
      if (data.error) {
        toast.error('Preview Error', {
          description: data.error
        });
        navigate(createPageUrl('Playbooks'));
        return;
      }

      setPreviewData({
        ...data,
        confirmed_attendees: Array.isArray(data.confirmed_attendees)
          ? data.confirmed_attendees
          : [],
        potential_attendees: Array.isArray(data.potential_attendees)
          ? data.potential_attendees
          : [],
      });

      console.log('🔍 API Response - message_stats:', data.message_stats);
      console.log('🔍 Sample confirmed persona:', data.confirmed_attendees?.[0]);
      console.log('🔍 Sample potential persona:', data.potential_attendees?.[0]);
      console.log('🔍 Personas with has_message_generated=true:', 
        [...(data.confirmed_attendees || []), ...(data.potential_attendees || [])]
        .filter(p => p.has_message_generated === true)
        .map(p => ({ name: `${p.first_name} ${p.last_name}`, has_message: p.has_message_generated, status: p.message_status }))
      );
      
      // Don't pre-select any personas by default - let user choose
      // Removed auto-selection of confirmed attendees
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      toast.error('Error', {
        description: error.message || 'Failed to load preview'
      });
    }
    setLoading(false);
  }
  
  async function handleCreateSequence() {
    console.log('🚀 handleCreateSequence called');
    console.log('📋 Selected personas:', selectedPersonaIds.length);
    
    if (selectedPersonaIds.length === 0) {
      console.warn('⚠️ No personas selected');
      toast.error('No personas selected', {
        description: 'Please select at least one persona'
      });
      return;
    }
    
    console.log('✅ Setting creating state to true');
    setCreating(true);
    let toastId;
    pollCancelledRef.current = false;
    blockedToastFiredRef.current = false;

    try {
      console.log('👤 Checking user:', { hasUser: !!user, userId: user?.id });
      if (!user?.id) {
        console.error('❌ User not loaded');
        toast.error('Error', {
          description: 'User not loaded. Please refresh the page.',
          variant: 'destructive'
        });
        setCreating(false);
        return;
      }

      console.log('📊 Checking previewData:', { hasPreviewData: !!previewData, hasEvent: !!previewData?.event });
      if (!previewData) {
        console.error('❌ Preview data not loaded');
        toast.error('Error', {
          description: 'Event data not loaded. Please refresh the page.',
          variant: 'destructive'
        });
        setCreating(false);
        return;
      }

      console.log('🌐 Making API request:', {
        url: `${API_BASE_URL}/api/event-playbooks/create`,
        eventId,
        playbookKey,
        tenantId,
        userId: user.id,
        selectedCount: selectedPersonaIds.length
      });
      
      const response = await fetch(`${API_BASE_URL}/api/event-playbooks/create`, {
        signal: typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(CREATE_REQUEST_TIMEOUT_MS) : undefined,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_id: eventId,
          playbook_key: playbookKey,
          tenant_id: tenantId,
          user_id: user.id,
          selected_persona_ids: selectedPersonaIds,
          use_signals: useSignals,
        }),
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'playbook_not_eligible') {
          toast.error('Playbook not eligible', {
            description: errorData.message || getEligibilityBlockMessage(errorData.reason),
          });
          setCreating(false);
          return;
        }
        console.error('❌ API error:', { status: response.status, error: errorData });
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      const resultCadence = normalizeCadenceAdaptation(result?.cadence_adaptation);
      setCreateCadenceAdaptation(resultCadence);
      console.log('📦 API result:', result);
      const sequenceIds = normalizeRunSequenceIds(result);
      const firstSequenceId = sequenceIds[0] || result.sequence_id || result.sequenceId || result.id;
      console.log('🔍 Sequence ids for polling:', {
        hasSuccess: result.success,
        sequenceIds,
        firstSequenceId,
        totalSteps: result.total_steps,
        stepsCreated: result.steps_created,
      });

      if (result.success && sequenceIds.length > 0) {
        console.log(
          '✅ Sequence(s) created:',
          sequenceIds.join(', '),
          result.multi_account ? '(multi-account)' : '(single)'
        );
        let totalSteps = result.total_steps || result.steps_created || 0;

        // Single-sequence legacy: kick off message generation when create returned no step counts
        if (
          sequenceIds.length === 1 &&
          !result.total_steps &&
          !result.steps_created
        ) {
          const onlyId = sequenceIds[0];
          console.log('🔄 Calling generate-messages endpoint');
          try {
            const generateResponse = await fetch(
              `${API_BASE_URL}/api/sequences/${onlyId}/generate-messages`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                signal:
                  typeof AbortSignal?.timeout === 'function'
                    ? AbortSignal.timeout(CREATE_REQUEST_TIMEOUT_MS)
                    : undefined,
                body: JSON.stringify({
                  tenant_id: tenantId,
                  user_id: user.id,
                }),
              }
            );

            console.log('📡 Generate-messages response:', generateResponse.status);

            if (generateResponse.ok) {
              const generateResult = await generateResponse.json();
              console.log('📦 Generate-messages result:', generateResult);
              if (generateResult.success) {
                totalSteps = generateResult.total_steps || totalSteps;
                console.log('✅ Updated totalSteps:', totalSteps);
              }
            } else {
              const errorText = await generateResponse.text();
              console.warn('⚠️ Generate-messages failed:', errorText);
            }
          } catch (generateError) {
            console.error('❌ Error calling generate-messages:', generateError);
          }
        }

        const eventName = previewData?.event?.event_name || 'the event';
        const expectedSecondsRun = Number(result.expected_seconds) || 300;
        console.log('📢 Showing toast for event:', eventName);
        try {
          toastId = toast.loading('🔄 Generating messages...', {
            description:
              totalSteps > 0
                ? `${formatGeneratingProgressLine({ complete: 0, total: 0 }, expectedSecondsRun, totalSteps)} · ${eventName}`
                : `Generating messages for ${eventName}…`,
            duration: Infinity,
          });
          console.log('✅ Toast shown:', toastId);
        } catch (toastError) {
          console.error('❌ Error showing toast:', toastError);
        }

        let pollCount = 0;
        const estimatedMessages =
          totalSteps > 0 ? totalSteps : selectedPersonaIds.length * 6;
        const watchWindowMs = computeWatchWindowMs(estimatedMessages);
        const watchStartedAt = Date.now();
        const statusLastBodyById = {};

        const pollStatus = async () => {
          if (pollCancelledRef.current) {
            console.log('⚠️ Component unmounted, stopping poll');
            return true;
          }

          try {
            pollCount += 1;
            const statusBodies = await fetchPlaybookStatusBodiesAllSettled(
              API_BASE_URL,
              sequenceIds,
              tenantId,
              user.id,
              statusLastBodyById
            );

            if (pollCancelledRef.current) {
              console.log('⚠️ Component unmounted, skipping UI updates');
              return true;
            }

            const parsedList = statusBodies.map(parsePlaybookStatusData);
            const phases = parsedList.map(sequencePollPhase);
            const agg = aggregateParsedList(parsedList);
            const blockedSteps = statusBodies.flatMap((b) =>
              Array.isArray(b?.blocked_steps) ? b.blocked_steps : []
            );
            const blockedCount = agg.blocked;

            console.log(
              `📊 Poll ${pollCount}: sequences=${sequenceIds.length}, complete=${agg.complete}/${agg.total}`
            );

            const failedSeq = phases.filter((p) => p === 'failed').length;
            if (failedSeq > 0) {
              toast.dismiss(toastId);
              toast.error('Generation failed', {
                description: describeSequencesFailed(sequenceIds.length, failedSeq, agg.complete),
              });
              setCreating(false);
              return true;
            }

            const noStepsSeq = phases.filter((p) => p === 'no_steps').length;
            if (noStepsSeq > 0) {
              toast.dismiss(toastId);
              toast.error('No steps to generate', {
                description:
                  sequenceIds.length > 1
                    ? `${noStepsSeq} sequence${noStepsSeq === 1 ? '' : 's'} had no steps.`
                    : 'This sequence has no steps yet.',
              });
              setCreating(false);
              return true;
            }

            const allDone = phases.every((p) => p === 'done');
            if (allDone) {
              toast.dismiss(toastId);
              if (blockedCount > 0 && !blockedToastFiredRef.current) {
                blockedToastFiredRef.current = true;
                setBlockedRetryCount(blockedCount);
                setBlockedRetryErrors({});
                setRetryingStepIds([]);
                setBlockedRetryItems(blockedSteps);
              } else {
                const failedTouchesText = agg.failed > 0 ? ` (${agg.failed} failed touches)` : '';
                toast.success('✅ Messages ready!', {
                  description: `Generated ${agg.complete} messages${failedTouchesText}. Redirecting to Outbox...`,
                });
              }
              setCreating(false);

              if (blockedCount === 0 && firstSequenceId) {
                console.log('🧭 Navigating to Outbox with sequence_id:', firstSequenceId);
                setTimeout(() => {
                  if (!pollCancelledRef.current) {
                    navigate(
                      {
                        pathname: createPageUrl('Outbox'),
                        search: `?sequence_id=${encodeURIComponent(firstSequenceId)}`,
                      },
                      { state: OUTBOX_POST_GENERATION_NAV_STATE }
                    );
                  }
                }, 1000);
              }
              return true;
            }

            toast.dismiss(toastId);
            toastId = toast.loading('🔄 Generating messages...', {
              description: `${formatGeneratingProgressLine(agg, expectedSecondsRun, estimatedMessages)} · ${eventName}`,
              duration: Infinity,
            });
          } catch (pollError) {
            if (pollError?.name === 'AbortError') {
              return true;
            }
            console.error(`❌ Poll ${pollCount} error:`, pollError);
            if (pollCancelledRef.current) {
              console.log('⚠️ Component unmounted during poll error');
              return true;
            }
          }
          
          return false;
        };
        
        // Initial poll after 2 seconds
        console.log('⏳ Waiting 2 seconds before first poll...');
        await sleep(INITIAL_POLL_DELAY_MS);
        
        if (pollCancelledRef.current) {
          console.log('⚠️ Component unmounted before first poll');
          return;
        }
        
        let done = await pollStatus();
        console.log('📊 First poll result:', done);
        
        while (!done && !pollCancelledRef.current) {
          const elapsedMs = Date.now() - watchStartedAt;
          if (elapsedMs >= watchWindowMs) {
            toast.dismiss(toastId);
            toast('Still running in the background', {
              description: 'Generation is still running in the background. Large runs can take longer. You can safely leave this page.'
            });
            setCreating(false);
            setTimeout(() => {
              if (!pollCancelledRef.current) {
                navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE });
              }
            }, 1500);
            break;
          }
          await sleep(getPollIntervalMs(elapsedMs));
          done = await pollStatus();
        }
        
      } else if (result.success) {
        // No sequence_id in response - this should be rare
        // Check if we have total_steps which indicates async generation is happening
        const messageCount = result.total_steps || result.steps_created || 0;
        
        console.log('⚠️ API returned success but no sequence_id:', {
          total_steps: result.total_steps,
          steps_created: result.steps_created,
          messageCount,
          fullResult: result
        });
        
        // If we have total_steps > 0, generation is happening async but we don't have sequence_id to poll
        // This is a backend issue - it should return sequence_id for async operations
        // For now, show loading and wait, then redirect (can't poll without sequence_id)
        if (messageCount > 0) {
          console.warn('⚠️ Got total_steps but no sequence_id - cannot poll status');
          toastId = toast.loading('🔄 Generating messages...', {
            description: `Generating ${messageCount} messages...`,
            duration: Infinity
          });
          
          const waitTime = computeWatchWindowMs(messageCount);
          console.log(`⏳ Waiting ${waitTime}ms for ${messageCount} messages (no sequence_id to poll)`);
          
          setTimeout(() => {
            if (pollCancelledRef.current) return;
            toast.dismiss(toastId);
            toast('Still running in the background', {
              description: 'Generation is still running in the background. Large runs can take longer. You can safely leave this page.'
            });
            setCreating(false);
            setTimeout(() => {
              if (!pollCancelledRef.current) {
                navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE });
              }
            }, 1000);
          }, waitTime);
        } else {
          // Truly synchronous completion (no messages to generate)
          console.log('✅ Sync completion - no messages to generate');
        toast.success('✅ Drafts created!', {
            description: 'Drafts created successfully'
        });
        setCreating(false);
        setTimeout(
          () => navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE }),
          1000
        );
        }
      } else {
        toast.error('Error', {
          description: result.error || 'Failed to create sequence',
          variant: 'destructive'
        });
        setCreating(false);
      }
    } catch (error) {
      console.error('❌ Failed to create sequence:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error details:', {
        message: error.message,
        name: error.name,
        cause: error.cause
      });
      
      if (pollCancelledRef.current) {
        console.log('⚠️ Component unmounted during error handling');
        return;
      }
      
      try {
      if (toastId) toast.dismiss(toastId);
      toast.error('Failed to create sequence', {
        description: error.message || 'Unknown error'
      });
      setCreating(false);
      } catch (toastError) {
        console.error('❌ Error showing error toast:', toastError);
      setCreating(false);
    }
    }
    
    // Note: Cleanup for pollInterval is handled in the polling logic itself
  }
  
  async function handleMarkAttending(personId) {
    try {
      if (!previewData?.event?.event_name) {
        console.error('Cannot mark attending: event data not available');
        return;
      }
      
      await fetch(`${API_BASE_URL}/api/event-playbooks/mark-attending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          person_id: personId,
          event_name: previewData.event.event_name,
          tenant_id: tenantId,
        }),
      });
      fetchPreview();
    } catch (error) {
      console.error('Failed to mark attending:', error);
    }
  }
  
  function togglePersona(personId) {
    setSelectedPersonaIds(prev => 
      prev.includes(personId)
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  }
  
  function selectAllConfirmed() {
    if (!previewData) return;
    const confirmedArr = Array.isArray(previewData.confirmed_attendees) ? previewData.confirmed_attendees : [];
    const confirmedIds = confirmedArr.map(p => p.person_id);
    setSelectedPersonaIds(prev => [...new Set([...prev, ...confirmedIds])]);
  }
  
  function deselectAllConfirmed() {
    if (!previewData) return;
    const confirmedArr = Array.isArray(previewData.confirmed_attendees) ? previewData.confirmed_attendees : [];
    const confirmedIds = confirmedArr.map(p => p.person_id);
    setSelectedPersonaIds(prev => prev.filter(id => !confirmedIds.includes(id)));
  }
  
  function selectAllPotential() {
    if (!previewData) return;
    const potentialArr = Array.isArray(previewData.potential_attendees) ? previewData.potential_attendees : [];
    const potentialIds = potentialArr.map(p => p.person_id);
    setSelectedPersonaIds(prev => [...new Set([...prev, ...potentialIds])]);
  }
  
  function getDaysUntilEvent(startDate) {
    const today = new Date();
    const eventDate = new Date(startDate);
    const diffTime = eventDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  
  function getDaysLabel(days) {
    if (days === 0) return 'Today!';
    if (days === 1) return 'Tomorrow';
    if (days < 0) return `${Math.abs(days)} days ago`;
    return `In ${days} days`;
  }
  
  function getDaysColor(days) {
    if (days <= 0) return 'text-red-500';
    if (days <= 7) return 'text-yellow-500';
    return 'text-green-500';
  }
  
  // Safety check: Log component state
  console.log('🎨 Component render:', {
    loading,
    hasPreviewData: !!previewData,
    creating,
    hasUser: !!user,
    eventId,
    playbookKey
  });
  
  if (!contextUser) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  if (loading) {
    console.log('⏳ Rendering loading state');
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }
  
  if (!previewData) {
    console.warn('⚠️ Rendering error state: no previewData');
    return (
      <div className="p-8">
        <button onClick={() => navigate(createPageUrl('Playbooks'))} className="flex items-center gap-2 mb-4 text-gray-600 hover:text-gray-900">
          <ChevronLeft size={20} />
          Back to Playbooks
        </button>
        <p className="text-center text-gray-500">Failed to load preview</p>
      </div>
    );
  }
  
  // Additional safety check for required data
  if (!previewData.event || !previewData.playbook) {
    console.error('❌ Preview data incomplete:', {
      hasEvent: !!previewData.event,
      hasPlaybook: !!previewData.playbook,
      previewDataKeys: Object.keys(previewData)
    });
    return (
      <div className="p-8">
        <button onClick={() => navigate(createPageUrl('Playbooks'))} className="flex items-center gap-2 mb-4 text-gray-600 hover:text-gray-900">
          <ChevronLeft size={20} />
          Back to Playbooks
        </button>
        <p className="text-center text-gray-500">Preview data incomplete. Please refresh.</p>
      </div>
    );
  }
  
  // Safe destructuring with defaults
  const { event, playbook } = previewData;
  const playbookDisplay =
    previewData.playbook_display ||
  null;
  const eligibility = previewData.eligibility || null;
  const displayMeta =
    getEventPlaybookDisplay(playbookKey) || EVENT_PLAYBOOK_DISPLAY[playbookKey] || null;
  const personaScopeLabel = (() => {
    const raw = playbookDisplay?.persona_scope ?? displayMeta?.persona_scope ?? null;
    if (String(raw || '').trim() === 'Confirmed attendees only') {
      return 'Previous and confirmed attendees only';
    }
    return raw;
  })();
  const personaScopeColor =
    displayMeta?.persona_scope_color ?? 'bg-blue-50 text-blue-700 border-blue-200';

  const timelineArr = Array.isArray(previewData.timeline) ? previewData.timeline : [];
  const confirmedArr = Array.isArray(previewData.confirmed_attendees) ? previewData.confirmed_attendees : [];
  const potentialArr = Array.isArray(previewData.potential_attendees) ? previewData.potential_attendees : [];
  const showConfirmed = confirmedArr.length > 0;
  const showPotential = potentialArr.length > 0;
  const requiresConfirmedOnly = Boolean(playbookDisplay?.requires_confirmed_attendees);
  const confirmedAttendeeCount =
    eligibility?.confirmed_attendees_count ??
    confirmedArr.length ??
    getEventConfirmedAttendeeCount(event);
  const message_stats = previewData.message_stats;
  const cadenceAdaptation = createCadenceAdaptation || normalizeCadenceAdaptation(previewData?.cadence_adaptation);
  const showCadenceNotice = !!cadenceAdaptation?.compressed;
  const droppedTouches = Array.isArray(cadenceAdaptation?.dropped) ? cadenceAdaptation.dropped : [];

  const daysUntil = getDaysUntilEvent(event.start_date);
  const validTouches = timelineArr.filter(t => t && !t.is_past);
  const totalMessages = selectedPersonaIds.length * validTouches.length;
  
  const personasForFilters = [...confirmedArr, ...potentialArr];
  const uniqueArchetypes = [
    ...new Set(personasForFilters.map((p) => p.archetype_name).filter(Boolean)),
  ];
  const uniqueDepartments = [
    ...new Set(personasForFilters.map((p) => p.department).filter(Boolean)),
  ];
  
  // Helper to get message status label
  const messageStatusLabel = (p) => {
    const status = (p.message_status || '').toLowerCase();
    if (!status) return 'Draft';
    if (status === 'draft_ready') return 'Draft';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };
  
  const filterAndSortPersonas = (personas) => {
    if (!personas) return [];
    
    let filtered = personas;
    
    // Apply search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.first_name?.toLowerCase().includes(q) ||
        p.last_name?.toLowerCase().includes(q) ||
        p.account_name?.toLowerCase().includes(q) ||
        p.role_title?.toLowerCase().includes(q)
      );
    }
    
    // Apply archetype filter
    if (filterArchetype !== 'all') {
      filtered = filtered.filter(p => p.archetype_name === filterArchetype);
    }
    
    // Apply department filter
    if (filterDepartment !== 'all') {
      filtered = filtered.filter(p => p.department === filterDepartment);
    }
    
    // Apply score filter
    if (filterScoreMin > 0) {
      filtered = filtered.filter(p => (p.persona_fit_score || 0) >= filterScoreMin);
    }
    
    // Apply "already messaged" filter
    if (hideWithMessages) {
      const beforeCount = filtered.length;
      filtered = filtered.filter(p => !isMessagedPersona(p));
      const afterCount = filtered.length;
      console.log(`Filter applied: ${beforeCount} -> ${afterCount} personas (hiding ${beforeCount - afterCount} already messaged)`);
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'persona_fit_score') {
        return (b.persona_fit_score || 0) - (a.persona_fit_score || 0);
      } else if (sortBy === 'name') {
        return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      }
      return 0;
    });
    
    return sorted;
  };
  
  function selectNextBatch(personas, batchSize = 40) {
    if (!personas || personas.length === 0) return;
    
    // Get filtered and sorted personas
    const filtered = filterAndSortPersonas(personas);
    
    // Find unselected personas
    const unselected = filtered.filter(p => !selectedPersonaIds.includes(p.person_id));
    
    // Select next batch (up to batchSize)
    const nextBatch = unselected.slice(0, batchSize);
    const nextBatchIds = nextBatch.map(p => p.person_id);
    
    if (nextBatchIds.length > 0) {
      setSelectedPersonaIds(prev => [...new Set([...prev, ...nextBatchIds])]);
      toast.success(`Selected ${nextBatchIds.length} personas`, {
        description: `${unselected.length - nextBatchIds.length} remaining unselected`
      });
    } else {
      toast.info('All personas already selected', {
        description: 'No more unselected personas in this list'
      });
    }
  }
  
  function selectNextBatchConfirmed() {
    if (!previewData) return;
    const confirmedArr = Array.isArray(previewData.confirmed_attendees) ? previewData.confirmed_attendees : [];
    selectNextBatch(confirmedArr, 40);
  }
  
  function selectNextBatchPotential() {
    if (!previewData) return;
    const potentialArr = Array.isArray(previewData.potential_attendees) ? previewData.potential_attendees : [];
    selectNextBatch(potentialArr, 40);
  }
  
  return (
    <div className="space-y-6">
      <button onClick={() => navigate(createPageUrl('Playbooks'))} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ChevronLeft size={20} />
        Back to Playbooks
      </button>
      
      {/* Event Header */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <CalendarDays className="text-blue-500" />
                {event.event_name}
              </h1>
              <p className="text-gray-600 mt-1 flex items-center gap-2">
                <MapPin size={16} />
                {event.city}, {Array.isArray(event.country) ? event.country[0] : event.country}
                {event.venue && ` • ${event.venue}`}
              </p>
              <p className="text-gray-500 mt-1">
                {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                {event.booth_number && ` • Booth #${event.booth_number}`}
              </p>
              <p className="text-sm text-[#5C6B7A] mt-2">
                <strong>{confirmedAttendeeCount}</strong> confirmed attendee
                {confirmedAttendeeCount === 1 ? '' : 's'}
                {playbookKey === 'event_quick_connect_v1' && (
                  <span className="block text-xs mt-0.5">
                    Quick Connect requires confirmed attendees.
                    {confirmedAttendeeCount === 0
                      ? ' Mark personas as attending below before creating a sequence.'
                      : ''}
                  </span>
                )}
              </p>
            </div>
            <div className={`text-lg font-semibold ${getDaysColor(daysUntil)}`}>
              {getDaysLabel(daysUntil)}
            </div>
          </div>
        </CardContent>
      </Card>

      {playbookDisplay && (
        <Card className="border-0 shadow-sm bg-gray-50/50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {personaScopeLabel && (
                <Badge variant="outline" className={personaScopeColor}>
                  {personaScopeLabel}
                </Badge>
              )}
              {playbookDisplay.requires_confirmed_attendees && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">
                  Confirmed attendees required
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium text-[#0A1A2F]">{playbookDisplay.purpose}</p>
            {playbookDisplay.voice && (
              <p className="text-xs text-gray-600">Voice: {playbookDisplay.voice}</p>
            )}
            {playbookDisplay.defining_anchor && (
              <p className="text-xs text-gray-600">Anchor: {playbookDisplay.defining_anchor}</p>
            )}
            {Number.isFinite(Number(playbookDisplay.best_lead_time_days)) &&
              Number(playbookDisplay.best_lead_time_days) > 0 && (
                <p className="text-xs text-gray-500">
                  Best lead time: first touch about T-{playbookDisplay.best_lead_time_days} days
                  before the event
                </p>
              )}
          </CardContent>
        </Card>
      )}

      <EventCustomPlaybookStatusBanner event={event} playbookKey={playbookKey} />

      {eligibility?.eligible === false && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm">
          <p className="font-medium text-amber-900">Create sequence is disabled for this event</p>
          <p className="mt-1 text-amber-900/80">
            {getEligibilityBlockMessage(eligibility.reason)}
          </p>
          {eligibility.eligible_personas_by_archetype &&
            Object.keys(eligibility.eligible_personas_by_archetype).length > 0 && (
              <ul className="mt-2 text-xs text-amber-900/80 list-disc pl-4">
                {Object.entries(eligibility.eligible_personas_by_archetype).map(([name, count]) => (
                  <li key={name}>
                    {name}: {count}
                  </li>
                ))}
              </ul>
            )}
          <p className="mt-2 text-xs text-amber-900/70">
            You can still review and select personas below; change playbook or audience to enable
            create.
          </p>
        </div>
      )}
      
      {/* Playbook & Timeline */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>{playbook.name}</CardTitle>
          <CardDescription>{playbook.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {showCadenceNotice && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">Cadence compressed for short lead time</p>
                  <p className="mt-1 text-xs leading-relaxed">
                    Your event is in {cadenceAdaptation.days_until_event} day(s). We adjusted the standard{' '}
                    {cadenceAdaptation.configured_pre_event_days}-day cadence to fit, with at most one
                    message per day.
                    {droppedTouches.length > 0
                      ? ` ${droppedTouches.length} pre-event touch(es) couldn't fit and were skipped.`
                      : ''}
                  </p>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-amber-800 hover:text-amber-900">
                        <HelpCircle size={16} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      <div>Configured pre-event days: {cadenceAdaptation.configured_pre_event_days}</div>
                      <div>Effective: {cadenceAdaptation.effective_lead_days}</div>
                      <div>
                        Dropped:{' '}
                        {droppedTouches.length > 0
                          ? droppedTouches
                              .map((d) => `T${d.order} (${channelLabel(d.channel)}, original day ${d.original_offset})`)
                              .join(', ')
                          : 'None'}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
          <h3 className="font-medium mb-2">Timeline ({validTouches.length} touches remaining)</h3>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {timelineArr.map((item, idx) => (
              <div 
                key={idx}
                className={`flex-shrink-0 p-3 rounded-lg border ${item.is_past ? 'bg-gray-100 opacity-50' : 'bg-blue-50 border-blue-200'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {item.touch?.channel === 'email' ? <Mail size={16} /> : <Linkedin size={16} />}
                  <span className="font-medium">Touch {item.touch?.order || idx + 1}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {item.scheduled_date ? new Date(item.scheduled_date).toLocaleDateString() : 'TBD'}
                </div>
                <div className="text-xs text-gray-500">
                  {(item.touch?.phase || '').replace('_', ' ')}
                </div>
                {item.is_past && <span className="text-xs text-red-500">Skipped</span>}
              </div>
            ))}
            {showCadenceNotice &&
              droppedTouches.map((d, idx) => {
                const order = Number(d?.order);
                const knownOrder = Number.isFinite(order) && order > 0;
                if (!knownOrder) return null;
                return (
                  <div
                    key={`dropped-${order}-${idx}`}
                    className="flex-shrink-0 p-3 rounded-lg border border-gray-200 bg-gray-100 text-gray-500"
                  >
                    <div className="mb-1 text-sm line-through">
                      Touch {order} ({channelLabel(d?.channel)})
                    </div>
                    <div className="text-xs">Skipped: not enough days before the event.</div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
      
      {/* Persona Selection */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex flex-col gap-4">
            {personaScopeLabel && (
              <p className="text-sm text-[#5C6B7A] bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                Audience scope: <strong>{personaScopeLabel}</strong>
                {showPotential && !showConfirmed && (
                  <span className="block text-xs mt-1 text-gray-500">
                    {potentialArr.length} potential attendee{potentialArr.length === 1 ? '' : 's'}{' '}
                    from the event (no confirmed attendees yet).
                  </span>
                )}
              </p>
            )}
            <div className="flex justify-between items-center">
              <CardTitle>Select Personas</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="search-personas"
                  name="search-personas"
                  type="text"
                  placeholder="Search personas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Message Filter Toggle */}
          <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={hideWithMessages} 
                  onCheckedChange={setHideWithMessages}
                  id="hide-messages-filter"
                />
                <Label 
                  htmlFor="hide-messages-filter" 
                  className="cursor-pointer text-sm font-medium leading-none select-none"
                >
                  Hide personas with generated messages
                </Label>
              </div>
              <span className="text-sm text-gray-600 ml-auto">
                {(() => {
                  const allPersonas = [...confirmedArr, ...potentialArr];
                  const messagedCount = allPersonas.filter(isMessagedPersona).length;
                  return `${messagedCount} of ${allPersonas.length} already messaged`;
                })()}
                {hideWithMessages && (
                  <Badge variant="outline" className="ml-2 text-green-600 border-green-300">
                    Filter ON
                  </Badge>
                )}
              </span>
            </div>
          </div>

          {!showConfirmed && !showPotential && (
            <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-600">
              <p className="font-medium">No personas in this preview</p>
              <p className="text-sm mt-1">
                {eligibility?.reason
                  ? getEligibilityBlockMessage(eligibility.reason)
                  : requiresConfirmedOnly
                    ? 'Mark personas as attending to populate confirmed attendees.'
                    : 'Try another playbook or refresh the preview.'}
              </p>
            </div>
          )}

          {/* Confirmed Attendees */}
          {showConfirmed && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium flex items-center gap-2">
                <Check className="text-green-500" size={18} />
                Previous and confirmed attendees ({filterAndSortPersonas(confirmedArr).length})
                {hideWithMessages && confirmedArr.length !== filterAndSortPersonas(confirmedArr).length && (
                  <span className="text-xs text-gray-400 font-normal">
                    (of {confirmedArr.length})
                  </span>
                )}
                {message_stats && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({message_stats.confirmed_with_messages} with messages)
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
              {(() => {
                  const confirmedIds = confirmedArr.map(p => p.person_id);
                const allConfirmedSelected = confirmedIds.length > 0 && 
                  confirmedIds.every(id => selectedPersonaIds.includes(id));
                return (
                  <Button 
                    onClick={allConfirmedSelected ? deselectAllConfirmed : selectAllConfirmed} 
                    variant="ghost" 
                    size="sm"
                  >
                    {allConfirmedSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                );
              })()}
                <Button 
                  onClick={selectNextBatchConfirmed} 
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                >
                  Select Next 40
                </Button>
              </div>
            </div>
            
            {confirmedArr.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-center">
                No confirmed attendees yet. Use "Mark Attending" below.
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                {filterAndSortPersonas(confirmedArr).map((persona) => (
                  <PersonaRow 
                    key={persona.person_id}
                    persona={persona}
                    selected={selectedPersonaIds.includes(persona.person_id)}
                    onToggle={() => togglePersona(persona.person_id)}
                    isConfirmed={true}
                  />
                ))}
              </div>
            )}
          </div>
          )}
          
          {/* Potential Attendees — VIP/Booth/Session; Quick Connect omits via BE empty array */}
          {showPotential && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium flex items-center gap-2">
                <HelpCircle className="text-yellow-500" size={18} />
                Potential Attendees ({filterAndSortPersonas(potentialArr).length})
                {hideWithMessages && potentialArr.length !== filterAndSortPersonas(potentialArr).length && (
                  <span className="text-xs text-gray-400 font-normal">
                    (of {potentialArr.length})
                  </span>
                )}
                {message_stats && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({message_stats.potential_with_messages} with messages)
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
              <Button onClick={selectAllPotential} variant="ghost" size="sm">
                Select All
              </Button>
                <Button 
                  onClick={selectNextBatchPotential} 
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                >
                  Select Next 40
              </Button>
              </div>
            </div>
            
            {/* Filters and Sort */}
            <div className="mb-3 p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                <SlidersHorizontal className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters & Sort</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label htmlFor="filter-score-min" className="text-xs text-gray-600 mb-1 block">Min Score</label>
                  <Input
                    id="filter-score-min"
                    name="filter-score-min"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={filterScoreMin || ''}
                    onChange={(e) => setFilterScoreMin(parseInt(e.target.value) || 0)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label htmlFor="filter-archetype" className="text-xs text-gray-600 mb-1 block">Archetype</label>
                  <select
                    id="filter-archetype"
                    name="filter-archetype"
                    value={filterArchetype}
                    onChange={(e) => setFilterArchetype(e.target.value)}
                    className="w-full h-9 px-3 border rounded-md text-sm"
                  >
                    <option value="all">All Archetypes</option>
                    {uniqueArchetypes.map(arch => (
                      <option key={arch} value={arch}>{arch}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-department" className="text-xs text-gray-600 mb-1 block">Department</label>
                  <select
                    id="filter-department"
                    name="filter-department"
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                    className="w-full h-9 px-3 border rounded-md text-sm"
                  >
                    <option value="all">All Departments</option>
                    {uniqueDepartments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="filter-sort-by" className="text-xs text-gray-600 mb-1 block">Sort By</label>
                  <select
                    id="filter-sort-by"
                    name="filter-sort-by"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full h-9 px-3 border rounded-md text-sm"
                  >
                    <option value="persona_fit_score">Fit Score (High to Low)</option>
                    <option value="name">Name (A-Z)</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="max-h-[600px] overflow-y-auto border rounded-lg">
              {filterAndSortPersonas(potentialArr).length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">
                  No personas match the current filters. Clear filters or turn off &quot;Hide
                  personas with generated messages&quot;.
                </p>
              ) : (
                filterAndSortPersonas(potentialArr).map((persona) => (
                  <PersonaRow 
                    key={persona.person_id}
                    persona={persona}
                    selected={selectedPersonaIds.includes(persona.person_id)}
                    onToggle={() => togglePersona(persona.person_id)}
                    isConfirmed={false}
                    onMarkAttending={() => handleMarkAttending(persona.person_id)}
                  />
                ))
              )}
            </div>
          </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary & Actions */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="space-y-2 text-sm">
            <li>• {selectedPersonaIds.length} personas selected</li>
            <li>• {validTouches.length} touches per persona</li>
            <li>• <strong>{totalMessages} total messages</strong> to generate</li>
          </ul>
          
          <label htmlFor="use-signals" className="flex items-center gap-2">
            <input
              id="use-signals"
              name="use-signals"
              type="checkbox"
              checked={useSignals}
              onChange={(e) => setUseSignals(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Use account/persona signals for personalization</span>
          </label>
          
          <div className="flex gap-4">
            <Button
              onClick={() => navigate(createPageUrl('Playbooks'))}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSequence}
              disabled={
                creating || selectedPersonaIds.length === 0 || eligibility?.eligible === false
              }
              title={
                eligibility?.eligible === false
                  ? getEligibilityBlockMessage(eligibility.reason)
                  : selectedPersonaIds.length === 0
                    ? 'Select at least one persona'
                    : undefined
              }
              className="bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              {eligibility?.eligible === false ? (
                'Playbook not eligible'
              ) : creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                `Create Event Sequence (${totalMessages} messages)`
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PersonaRow({ 
  persona, 
  selected, 
  onToggle, 
  isConfirmed,
  onMarkAttending 
}) {
  const navigate = useNavigate();

  const handleMessageClick = (e) => {
    e.stopPropagation();
    if (persona.sequence_step_id) {
      // Navigate to Outbox with the specific step ID
      navigate(createPageUrl('Outbox') + `?draft=${persona.sequence_step_id}`);
    } else if (persona.person_id) {
      // Fallback: navigate to Outbox and search for the person
      const personName = `${persona.first_name} ${persona.last_name}`;
      navigate(createPageUrl('Outbox') + `?search=${encodeURIComponent(personName)}`);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between p-1.5 md:p-2 border-b hover:bg-gray-50 gap-1.5 md:gap-2">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <input
          id={`persona-checkbox-${persona.person_id}`}
          name={`persona-checkbox-${persona.person_id}`}
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="rounded flex-shrink-0"
          aria-label={`Select ${persona.first_name} ${persona.last_name}`}
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm break-words leading-tight flex items-center gap-1.5">
            <span>{persona.first_name} {persona.last_name}</span>
            {isConfirmed && (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Ticket className="h-3 w-3 text-green-500" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-[#0A1A2F] text-white border-0 shadow-lg">
        <p className="text-xs">Confirmed attendee</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)}
            {(() => {
              const status = (persona.message_status || '').toLowerCase();
              const hasMessaged = Boolean(persona.has_message_generated) || ['draft', 'draft_ready', 'scheduled', 'sent'].includes(status);
              const statusLabel =
                status === 'draft_ready' ? 'Draft' :
                status ? status.charAt(0).toUpperCase() + status.slice(1) :
                'Draft';

              return hasMessaged && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleMessageClick}
                        className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                        title="View message in Outbox"
                      >
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-[10px] md:text-xs text-green-700">{statusLabel}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-[#0A1A2F] text-white border-0 shadow-lg">
                      <p className="text-xs font-semibold mb-1 text-[#00C2C2]">Already Messaged</p>
                      <p className="text-xs">Status: {statusLabel}</p>
                      <p className="text-xs mt-1 text-gray-300">Click to view in Outbox</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()}
          </div>
          <div className="text-xs text-gray-500 break-words line-clamp-1">
            {persona.role_title} • {persona.account_name}
          </div>
          <div className="text-xs text-gray-400 flex flex-wrap items-center gap-1 mt-0.5">
            <span className="flex items-center gap-0.5 whitespace-nowrap">
              <MapPin size={10} />
              {persona.country}
            </span>
            {persona.department && (
              <span className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 whitespace-nowrap text-[10px]">
                {persona.department}
              </span>
            )}
            {persona.archetype_name && (
              <span className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 whitespace-nowrap text-[10px]">
                {persona.archetype_name}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-1.5 flex-shrink-0 ml-8 md:ml-2">
        {persona.persona_fit_score && (
          <Badge variant="secondary" className="text-[10px] whitespace-nowrap px-1.5 py-0.5">
            Fit: {persona.persona_fit_score}
          </Badge>
        )}
        {!isConfirmed && onMarkAttending && (
          <Button
            onClick={(e) => { e.stopPropagation(); onMarkAttending(); }}
            variant="ghost"
            size="sm"
            className="text-[10px] text-green-600 hover:text-green-700 whitespace-nowrap h-6 px-1.5"
          >
            Mark Attending
          </Button>
        )}
      </div>
    </div>
  );
}